// Phase 999.4 Plan 05 — Sites table (R2 + D-17).
//
// Read-only table fetched from GET /api/admin/sites (Plan 02). Columns: Org Name,
// Location Name, LocationID, HardwareID, Hardware.Name. Server returns rows
// already sorted Org -> Location -> HardwareID asc — DO NOT re-sort client-side
// (RESEARCH anti-pattern). Whole-row click navigates to /admin/sites/:locationId/config
// (D-17 — full row hit-target; cursor pointer; role='button' tabIndex={0}; keyboard
// activation via Enter/Space mirrors the click handler for accessibility).
//
// Phase 260522-go3: "Show inactive" checkbox toggles ?includeInactive=true on the
// fetch URL. Inactive rows are shown with muted opacity when visible.
//
// SECURITY: Bearer JWT in Authorization header; the real authorization gate is
// server-side `[Authorize(Policy = "GlobalAdmin")]` on /api/admin/sites (Plan 02).
// AdminGuard on the route is UX-only.
//
// XSS: `{r.hardwareName}` etc. rely on React's default text-content escaping —
// no dangerouslySetInnerHTML, no string concatenation into innerHTML.

import React, { useState, useCallback, useEffect } from 'react';
import { Container, Row, Col, Table, Button, Alert } from 'reactstrap';
import { useHistory } from 'react-router-dom';
import { apiBase } from './apiBase';

const COLUMNS = [
    { key: 'orgName',      name: 'Org Name' },
    { key: 'locationName', name: 'Location Name' },
    { key: 'locationId',   name: 'LocationID' },
    { key: 'hardwareId',   name: 'HardwareID' },
    { key: 'hardwareName', name: 'Hardware.Name' },
];

export function SitesTable({ auth }) {
    const [rows, setRows]               = useState([]);
    const [loading, setLoading]         = useState(false);
    const [error, setError]             = useState(null);
    const [showInactive, setShowInactive] = useState(false);
    const history = useHistory();

    const fetchRows = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            // auth may be missing in unit tests — guard so the component still
            // renders. The real /admin route always passes it through AdminLayout.
            let accessToken = '';
            if (auth && typeof auth.getAccessToken === 'function') {
                try { accessToken = auth.getAccessToken(); } catch (e) { /* ignore — server will 401 */ }
            }
            const url = apiBase() + '/api/admin/sites' + (showInactive ? '?includeInactive=true' : '');
            const res = await fetch(url, {
                method: 'GET',
                headers: accessToken ? { Authorization: 'Bearer ' + accessToken } : {},
            });
            if (!res.ok) {
                const text = await res.text().catch(() => '');
                throw new Error('HTTP ' + res.status + (text ? ': ' + text : ''));
            }
            const data = await res.json();
            // Preserve server-provided ordering (Org -> Location -> HardwareID asc).
            // No client-side reordering here — RESEARCH §Anti-Patterns explicitly
            // prohibits resorting after fetch because it can mask server-sort regressions.
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            setError(e && e.message ? e.message : String(e));
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [auth, showInactive]);

    useEffect(() => { fetchRows(); }, [fetchRows]);

    // SPA is mounted at root on admin.<host> (per Phase 999.1 subdomain refactor),
    // so URLs are /sites/... not /admin/sites/.... If this SPA ever gets remounted
    // under /admin, AdminLayout.baseUrl needs to be threaded down through here.
    const goToConfig = (locationId) => history.push(`/sites/${locationId}/config`);

    return (
        <Container fluid className="sites-table" data-testid="sites-table">
            <Row className="mb-3 align-items-center">
                <Col><h2 className="mb-0">Sites</h2></Col>
                <Col xs="auto">
                    <label className="me-3" style={{ fontSize: '0.875rem' }}>
                        <input
                            type="checkbox"
                            checked={showInactive}
                            onChange={(e) => setShowInactive(e.target.checked)}
                            data-testid="sites-show-inactive"
                            style={{ marginRight: '0.375rem' }}
                        />
                        Show inactive sites
                    </label>
                    <Button
                        color="secondary"
                        size="sm"
                        onClick={fetchRows}
                        disabled={loading}
                        data-testid="sites-refresh"
                    >
                        {loading ? 'Refreshing...' : 'Refresh'}
                    </Button>
                </Col>
            </Row>
            {error && (
                <Alert color="danger" data-testid="sites-error">
                    Failed to load Sites: {error}
                </Alert>
            )}
            <div style={{ overflowX: 'auto' }} data-testid="sites-grid">
                <Table size="sm" bordered hover responsive={false}>
                    <thead>
                        <tr>
                            {COLUMNS.map((c) => (
                                <th key={c.key} style={{ whiteSpace: 'nowrap' }}>{c.name}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr>
                                <td colSpan={COLUMNS.length} className="text-center text-muted">
                                    {loading ? 'Loading...' : 'No sites yet.'}
                                </td>
                            </tr>
                        ) : (
                            rows.map((r) => {
                                // hardwareId === 0 is the COALESCE placeholder for sites
                                // that have no hardware rows joined (LEFT JOIN result on
                                // the server). Render a "(no hardware)" cell so they still
                                // appear in the table and can be navigated to /config to
                                // toggle Active. Compound key avoids React collisions when
                                // multiple hardware-less sites all have hardwareId=0.
                                const noHardware = r.hardwareId === 0;
                                return (
                                    <tr
                                        key={`${r.locationId}-${r.hardwareId}`}
                                        role="button"
                                        tabIndex={0}
                                        style={{
                                            cursor: 'pointer',
                                            opacity: r.isActive === false ? 0.45 : undefined,
                                            fontStyle: r.isActive === false ? 'italic' : undefined,
                                        }}
                                        onClick={() => goToConfig(r.locationId)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                goToConfig(r.locationId);
                                            }
                                        }}
                                        data-testid={`sites-row-${r.locationId}-${r.hardwareId}`}
                                    >
                                        <td>{r.orgName}</td>
                                        <td>{r.locationName}</td>
                                        <td>{r.locationId}</td>
                                        <td>{noHardware ? <span className="text-muted">—</span> : r.hardwareId}</td>
                                        <td>{noHardware ? <span className="text-muted fst-italic">(no hardware)</span> : r.hardwareName}</td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </Table>
            </div>
        </Container>
    );
}

export default SitesTable;
